import React, { useState, useEffect, useCallback } from "react";
import type { StrategyNode } from "../../../types/BaseNode";
import { useQuickTuneState, type PendingChange } from "../../../hooks/useQuickTuneState";
import { useStrategyPreview } from "../../../hooks/useStrategyPreview";
import { Responsive, WidthProvider } from "react-grid-layout";
import type { Layouts } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { QuickTunePanel } from "../quicktune/QuickTunePanel";
import { extractTunableParams } from "../../../utils/extractTunableParams";
import { serializeNode } from "../../../utils/serializeNode";
import { getColor } from "../../../utils/colorUtils";
import type { TunableParam } from "../../../types/TunableParam";
import { API_BASE } from "../../../config";

// Modular Widgets
import { EvaluateHeader } from "./EvaluateHeader";
import { getStrategy } from "./strategies";
import { ParityReportWidget } from "./ParityReportWidget";
import { EmptyState } from "../common/EmptyState";

const ResponsiveGridLayout = WidthProvider(Responsive);

function filterLayoutsToActiveWidgets(layouts: Layouts, activeWidgets: string[]): Layouts {
  const activeSet = new Set(activeWidgets);
  const filteredEntries = Object.entries(layouts).map(([breakpoint, items]) => [
    breakpoint,
    items.filter((item) => activeSet.has(item.i)),
  ]);
  return Object.fromEntries(filteredEntries) as Layouts;
}

interface Props {
  rootNode: StrategyNode | null;
  result: any | null;
  error: string | null;
  isLoading: boolean;
  onEvaluate: (nodeOverride?: StrategyNode | null) => void;
  onCancelMeepMeep?: () => void;
  overlays: any[];
  setOverlays: (overlays: any[]) => void;
  nature?: string;  // Data nature from backend
  display_type?: string;  // e.g. 'candlestick' — rendering hint from backend
  globalStart: string | null;
  globalEnd: string | null;
  strategyName?: string;
  categorical_labels?: Record<number, string>;  // Dynamic categorical labels from backend
  aiReport?: any;
  setAiReport?: (report: any | null) => void;
  activeWidgets?: string[];
  setActiveWidgets?: (widgets: string[]) => void;
  backendStats?: Record<string, number | null> | null;
  chartData?: any[] | null;
  chartSeriesLabels?: Record<string, string> | null;
  parityReport?: any;
  loadingMode?: 'evaluate' | 'quantarc' | 'meepmeep' | null;
  meepMeepJobMessage?: string | null;
  meepMeepJobStage?: string | null;
  meepMeepCurrentDayCount?: number | null;
  meepMeepTotalDayCount?: number | null;
  meepMeepProgressPct?: number | null;
  meepMeepRequestTree?: any | null;
  selectedMeepMeepDate?: string | null;
  setSelectedMeepMeepDate?: (date: string | null) => void;
}

export function EvaluatePanel({
  rootNode, result, error, isLoading, onEvaluate, onCancelMeepMeep, overlays, setOverlays, nature, display_type, globalStart, globalEnd, strategyName = "Original", categorical_labels,
  aiReport, setAiReport, activeWidgets: controlledActiveWidgets, setActiveWidgets: controlledSetActiveWidgets, backendStats, chartData, chartSeriesLabels,
  parityReport, loadingMode, meepMeepJobMessage, meepMeepJobStage, meepMeepCurrentDayCount, meepMeepTotalDayCount, meepMeepProgressPct, meepMeepRequestTree,
  selectedMeepMeepDate, setSelectedMeepMeepDate
}: Props) {
  const effectiveNature = React.useMemo(() => {
    if (nature) return nature;
    if (rootNode?.type === 'MeepMeep') return 'meepmeep_result';
    return undefined;
  }, [nature, rootNode]);
  const strategy = React.useMemo(() => getStrategy(effectiveNature), [effectiveNature]);
  const [localActiveWidgets, setLocalActiveWidgets] = useState<string[]>(() => strategy.getDefaultWidgets());
  const activeWidgets = controlledActiveWidgets ?? localActiveWidgets;
  const setActiveWidgets = controlledSetActiveWidgets ?? setLocalActiveWidgets;

  // Sync widgets ONLY when nature actually changes (e.g. switching from standard data to insight)
  const prevNature = React.useRef(effectiveNature);
  useEffect(() => {
    if (effectiveNature !== prevNature.current) {
      setActiveWidgets(strategy.getDefaultWidgets());
      prevNature.current = effectiveNature;
    }
  }, [effectiveNature, strategy, setActiveWidgets]);

  const [tunableParams, setTunableParams] = useState<TunableParam[]>([]);
  const [showQuickTune, setShowQuickTune] = useState(false);
  const [tunedResult, setTunedResult] = useState<any[] | null>(null);
  const [localAiReport, setLocalAiReport] = useState<any | null>(null);
  const currentAiReport = aiReport !== undefined ? aiReport : localAiReport;
  const setCurrentAiReport = setAiReport ?? setLocalAiReport;

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [focusQuestion, setFocusQuestion] = useState("");
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  // Clear runtime errors and AI report when calculating results to avoid stale explanations
  useEffect(() => {
    if (isLoading) {
      setAnalyzeError(null);
      setCurrentAiReport(null);
      setSelectedMeepMeepDate?.(null);
      void import("./MeepMeepWidgets").then(({ clearMeepMeepDetailCache }) => {
        clearMeepMeepDetailCache();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Extract tunable parameters
  useEffect(() => {
    (async () => {
      if (rootNode) {
        const params = await extractTunableParams(rootNode);
        setTunableParams(params);
        setShowQuickTune(params.length > 0);
      } else {
        setTunableParams([]);
        setShowQuickTune(false);
      }
    })();
  }, [rootNode]);

  const { pendingChanges, addChange, resetChanges } = useQuickTuneState();
  const [appliedChanges, setAppliedChanges] = useState<Map<string, PendingChange>>(new Map());

  useEffect(() => {
    setAppliedChanges(new Map());
  }, [rootNode]);

  const { previewRoot, previewResult } = useStrategyPreview({
    originalRoot: rootNode,
    changes: appliedChanges,
    globalStart,
    globalEnd
  });

  useEffect(() => {
    setTunedResult(previewResult);
  }, [previewResult]);

  const handleApplyChanges = useCallback((changes: any[]) => {
    const newApplied = new Map<string, PendingChange>();
    changes.forEach((c: PendingChange) => {
      newApplied.set(`${c.nodeId}:${c.paramKey}`, c);
    });
    setAppliedChanges(newApplied);
  }, []);

  const handleSaveComparison = useCallback((name: string) => {
    if (!previewRoot || !previewResult) return;

    const newOverlay = {
      id: "strategy-comparison",
      label: name,
      name: `Comparison: ${name}`,
      params: {
        tree: JSON.stringify(serializeNode(previewRoot, globalStart, globalEnd)),
        start: globalStart,
        end: globalEnd
      },
      result: {
        columns: [{ id: 'time', type: 'datetime' }, { id: 'value', type: 'number' }],
        data: previewResult
      },
      color: getColor(overlays.length + 1)
    };

    setOverlays([...overlays, newOverlay]);
    resetChanges();
    setAppliedChanges(new Map());
  }, [previewRoot, previewResult, overlays, setOverlays, resetChanges, globalStart, globalEnd]);

  const handleAnalyze = useCallback(async () => {
    if (!rootNode) return;
    setIsAnalyzing(true);
    setAnalyzeError(null);
    try {
      const serialized = serializeNode(rootNode, globalStart, globalEnd);
      const resp = await fetch(`${API_BASE}/strategy/ai/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          strategy: serialized,
          user_prompt: focusQuestion || undefined,
          context: globalStart && globalEnd ? { start_date: globalStart, end_date: globalEnd } : undefined,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setCurrentAiReport(data.report);
        if (!activeWidgets.includes("ai-insights")) {
          setActiveWidgets([...activeWidgets, "ai-insights"]);
        }
      } else {
        setAnalyzeError(data.error ?? "Analysis failed.");
      }
    } catch (e: any) {
      setAnalyzeError(e.message ?? "Network error.");
    } finally {
      setIsAnalyzing(false);
    }
  }, [rootNode, focusQuestion, globalStart, globalEnd, activeWidgets]);

  const toggleWidget = (widget: string) => {
    setActiveWidgets(
      activeWidgets.includes(widget) ? activeWidgets.filter((w) => w !== widget) : [...activeWidgets, widget]
    );
  };

  const average = Array.isArray(result)
    ? (() => {
        const values = result
          .map((row: any) => row?.value)
          .filter((value: unknown): value is number => typeof value === 'number' && Number.isFinite(value));
        if (values.length === 0) return 0;
        return values.reduce((sum: number, value: number) => sum + value, 0) / values.length;
      })()
    : 0;

  const layouts: Layouts = React.useMemo(
    () => filterLayoutsToActiveWidgets(strategy.getLayout(activeWidgets), activeWidgets),
    [strategy, activeWidgets]
  );

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", background: 'var(--bg-app)' }}>
      <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "1rem" }}>
          <EvaluateHeader
            isLoading={isLoading}
            onEvaluate={onEvaluate}
            onCancelMeepMeep={loadingMode === 'meepmeep' ? onCancelMeepMeep : undefined}
            loadingMode={loadingMode}
            loadingMessage={meepMeepJobMessage}
            loadingStage={meepMeepJobStage}
            currentDayCount={meepMeepCurrentDayCount}
            totalDayCount={meepMeepTotalDayCount}
            progressPct={meepMeepProgressPct}
          />

          {/* AI Analyze bar */}
          {result && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1, minWidth: "300px" }}>
              <input
                type="text"
                placeholder="Optional focus question (e.g. correlate linepack and price)…"
                value={focusQuestion}
                onChange={(e) => setFocusQuestion(e.target.value)}
                style={{
                  flex: 1,
                  padding: "0.4rem 0.75rem",
                  background: "var(--bg-card)",
                  border: "1px solid var(--surface-glass-border)",
                  borderRadius: "var(--radius-sm)",
                  color: "var(--text-primary)",
                  fontSize: "0.8rem",
                }}
              />
              <button
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                style={{
                  padding: "0.4rem 1rem",
                  background: isAnalyzing ? "var(--accent-glow)" : "var(--accent-primary)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--accent-primary-hover)",
                  borderRadius: "var(--radius-sm)",
                  cursor: isAnalyzing ? "not-allowed" : "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  transition: "background 0.2s ease",
                }}
              >
                {isAnalyzing ? "⏳ Analyzing…" : "🤖 Analyze"}
              </button>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            minWidth: 0,
            paddingBottom: "0.15rem",
          }}
        >
          <div
            style={{
              fontSize: "0.72rem",
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
          >
            Views
          </div>
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              overflowX: "auto",
              overflowY: "hidden",
              minWidth: 0,
              flex: 1,
              paddingBottom: "0.2rem",
              scrollbarWidth: "thin",
            }}
          >
            {strategy.getAvailableWidgets(!!currentAiReport).map(widget => (
              <WidgetToggle
                key={widget.id}
                active={activeWidgets.includes(widget.id)}
                onClick={() => toggleWidget(widget.id)}
              >
                {widget.label}
              </WidgetToggle>
            ))}
          </div>
        </div>

        {error && <p style={{ color: "var(--accent-danger)", margin: 0, fontSize: '0.8rem' }}>❌ {error}</p>}
        {analyzeError && <p style={{ color: "var(--accent-danger)", margin: 0, fontSize: '0.8rem' }}>❌ {analyzeError}</p>}
      </div>

      {result && (nature === 'insight' || nature === 'meepmeep_result' || (Array.isArray(result) && result.length > 0)) && (
        <div style={{ flex: 1, overflow: "auto" }}>
          {parityReport && (
            <div style={{ padding: '1rem 1rem 0 1rem' }}>
              <ParityReportWidget 
                status={parityReport.ui?.status ?? parityReport.status}
                export_blocked={parityReport.ui?.export_blocked ?? parityReport.export_blocked ?? false}
                strategyName={parityReport.ui?.strategyName ?? (parityReport.strategyName || strategyName)}
                date={parityReport.ui?.date ?? (parityReport.date || new Date().toISOString())}
                metrics={parityReport.ui?.metrics ?? (parityReport.metrics || [])}
                deltas={parityReport.ui?.deltas ?? (parityReport.deltas || [])}
              />
            </div>
          )}
          <div className="grid-background" style={{ padding: '0 1rem 1rem 1rem' }}>
            <ResponsiveGridLayout
              className="layout"
              layouts={layouts}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={100}
              compactType="vertical"
              draggableHandle=".widget-header"
              margin={[12, 12]}
            >
              {activeWidgets.map(widgetId => (
                <div key={widgetId}>
                  <MemoizedWidget
                    widgetId={widgetId}
                    strategy={strategy}
                    result={result}
                    average={average}
                    overlays={overlays}
                    setOverlays={setOverlays}
                    tunedResult={tunedResult}
                    strategyName={strategyName}
                    categorical_labels={categorical_labels}
                    display_type={display_type}
                    nature={nature}
                    globalStart={globalStart}
                    globalEnd={globalEnd}
                    currentAiReport={currentAiReport}
                    backendStats={backendStats}
                    chartData={chartData}
                    chartSeriesLabels={chartSeriesLabels}
                    selectedMeepMeepDate={selectedMeepMeepDate}
                    setSelectedMeepMeepDate={setSelectedMeepMeepDate}
                    rootNode={rootNode}
                    meepMeepRequestTree={meepMeepRequestTree}
                  />
                </div>
              ))}
            </ResponsiveGridLayout>
          </div>
        </div>
      )}

      {showQuickTune && (
        <QuickTunePanel
          tunableParams={tunableParams}
          onApplyChanges={handleApplyChanges}
          onSaveComparison={handleSaveComparison}
          onCollapse={() => {
            setTunedResult(null);
            setAppliedChanges(new Map());
          }}
          pendingChanges={pendingChanges}
          onLocalChange={addChange}
          onReset={resetChanges}
          saveDisabled={!previewResult}
        />
      )}

      {(!isLoading && !error && nature !== 'insight' && Array.isArray(result) && result.length === 0) && (
        <div style={{ padding: '2rem', height: '100%', display: 'flex' }}>
          <EmptyState 
            title="No Data" 
            description="The strategy evaluates successfully but returned no data. Try adjusting the dates, filters, or input parameters." 
            icon="📂" 
          />
        </div>
      )}
    </div>
  );
}

function WidgetToggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "0.5rem 1rem",
        backgroundColor: active ? "var(--accent-primary)" : "var(--bg-card)",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        border: "1px solid var(--surface-glass-border)",
        borderRadius: "var(--radius-sm)",
        cursor: "pointer",
        fontSize: '0.875rem',
        fontWeight: '500',
        transition: 'all 0.2s ease'
      }}
    >
      {children}
    </button>
  );
}

const MemoizedWidget = React.memo(function MemoizedWidget({
  widgetId,
  strategy,
  ...props
}: { widgetId: string; strategy: ReturnType<typeof getStrategy> } & Parameters<ReturnType<typeof getStrategy>['renderWidget']>[1]) {
  const node = strategy.renderWidget(widgetId, props as any);
  return node ? <>{node}</> : null;
});
